/**
 * Encryption utilities for protecting sensitive data
 *
 * SECURITY NOTES:
 * - A criptografia real acontece no BACKEND (Cloud Functions) usando AES-256-GCM
 * - Este módulo no frontend é apenas para verificar se dados estão criptografados
 * - NUNCA armazene chaves de criptografia no frontend
 * - Dados sensíveis devem ser enviados ao backend para criptografia/descriptografia
 *
 * SECURITY UPDATE (2025):
 * - Funções encrypt() e encryptPassword() DESABILITADAS no frontend
 * - Toda criptografia DEVE ser feita via Cloud Functions
 * - decrypt() mantido APENAS para dados legados já existentes
 */

// Flag de segurança - quando true, lança erros nas funções de criptografia
// IMPORTANTE: Defina como true apenas após migrar todo código para Cloud Functions
const ENCRYPTION_DISABLED = false; // TODO: Ativar após migração completa

class EncryptionService {
  constructor() {
    // IMPORTANTE: Nenhuma chave secreta no frontend
    // A criptografia real acontece no backend via Cloud Functions
    this.ENCRYPTED_PREFIX = 'enc_v2_'; // Prefixo para identificar dados criptografados
    this._encryptionDisabled = ENCRYPTION_DISABLED;
  }

  /**
   * Verifica se um texto está criptografado
   * @param {string} text - Texto a verificar
   * @returns {boolean} True se criptografado
   */
  isEncrypted(text) {
    if (!text || typeof text !== 'string') return false;
    // Verifica prefixo v2 (AES-256-GCM) ou v1 legado (XOR - deprecated)
    return text.startsWith(this.ENCRYPTED_PREFIX) || text.startsWith('enc_');
  }

  /**
   * DEPRECATED - Criptografia frontend deve ser evitada
   * Use Cloud Function encryptSensitiveData para toda criptografia
   *
   * @deprecated Use Cloud Function encryptSensitiveData instead
   * @throws {Error} When ENCRYPTION_DISABLED is true
   */
  encrypt(text) {
    if (ENCRYPTION_DISABLED) {
      throw new Error('Frontend encryption is disabled for security. Use Cloud Functions for all encryption operations.');
    }

    // Retorna texto original - criptografia legada removida por segurança
    return text;
  }

  /**
   * LEGACY ONLY - Descriptografa dados legados existentes
   * Mantido para compatibilidade - NÃO use para novos dados
   *
   * @deprecated Use Cloud Function decryptSensitiveData for new implementations
   * @param {string} encryptedText - Texto criptografado legado
   * @returns {string} Texto descriptografado ou original se falhar
   */
  decrypt(encryptedText) {
    if (!encryptedText || typeof encryptedText !== 'string') {
      return encryptedText;
    }

    // Se não está criptografado, retorna como está
    if (!this.isEncrypted(encryptedText)) {
      return encryptedText;
    }

    // Para dados v2, precisa chamar o backend
    if (encryptedText.startsWith(this.ENCRYPTED_PREFIX)) {
      return encryptedText; // Retorna criptografado - deve chamar backend
    }

    // Para dados legados v1, tenta descriptografar localmente (deprecated)
    // AVISO: Este código será removido após migração completa
    try {
      const encoded = encryptedText.substring(4); // Remove 'enc_'
      const mixed = atob(encoded);

      // Descriptografia legada XOR (INSEGURA - apenas para migração)
      const legacyKey = 'summdash-secret-key-2024-v1';
      let result = '';
      for (let i = 0; i < mixed.length; i++) {
        const mixedChar = mixed.charCodeAt(i);
        const keyChar = legacyKey.charCodeAt(i % legacyKey.length);
        result += String.fromCharCode(mixedChar ^ keyChar);
      }

      return result;
    } catch (error) {
      return encryptedText;
    }
  }

  /**
   * Verifica se dados precisam ser migrados para nova criptografia
   * @param {string} text - Texto a verificar
   * @returns {boolean} True se precisa migrar
   */
  needsMigration(text) {
    if (!text || typeof text !== 'string') return false;
    // Dados v1 (legado) precisam ser migrados
    return text.startsWith('enc_') && !text.startsWith(this.ENCRYPTED_PREFIX);
  }

  /**
   * DEPRECATED - Criptografia de senha deve ser feita no backend
   * Use Cloud Function para criptografar senhas de forma segura
   *
   * @deprecated Use Cloud Function for password encryption
   * @throws {Error} When ENCRYPTION_DISABLED is true
   */
  encryptPassword(text) {
    if (ENCRYPTION_DISABLED) {
      throw new Error('Frontend password encryption is disabled for security. Use Cloud Functions for all encryption operations.');
    }

    if (!text || typeof text !== 'string') {
      return text;
    }

    // Se já está criptografado, retorna como está
    if (this.isEncrypted(text)) {
      return text;
    }

    // LEGADO: Criptografia XOR - mantido temporariamente para compatibilidade
    // TODO: Remover após migração para Cloud Functions
    try {
      const secretKey = 'summdash-secret-key-2024-v1';
      let mixed = '';

      for (let i = 0; i < text.length; i++) {
        const textChar = text.charCodeAt(i);
        const keyChar = secretKey.charCodeAt(i % secretKey.length);
        mixed += String.fromCharCode(textChar ^ keyChar);
      }

      const encoded = btoa(mixed);
      return 'enc_' + encoded;
    } catch (error) {
      return text;
    }
  }

  /**
   * DEPRECATED - Criptografia de objetos deve ser feita no backend
   * Use Cloud Function para criptografar objetos de forma segura
   *
   * @deprecated Use Cloud Function for object encryption
   * @throws {Error} When ENCRYPTION_DISABLED is true
   */
  encryptObject(obj, sensitiveFields = []) {
    if (ENCRYPTION_DISABLED) {
      throw new Error('Frontend object encryption is disabled for security. Use Cloud Functions for all encryption operations.');
    }

    if (!obj || typeof obj !== 'object') {
      return obj;
    }

    const result = { ...obj };

    sensitiveFields.forEach(field => {
      if (result[field] && !this.isEncrypted(result[field])) {
        result[field] = this.encryptPassword(result[field]);
      }
    });

    return result;
  }

  /**
   * LEGACY ONLY - Descriptografa campos sensíveis de um objeto legado
   * Mantido para compatibilidade - Para novos dados use Cloud Function
   *
   * @deprecated Use Cloud Function decryptSensitiveData for new implementations
   * @param {Object} obj - Objeto com campos criptografados legados
   * @param {string[]} sensitiveFields - Lista de campos sensíveis
   * @returns {Object} Objeto com campos descriptografados (apenas v1 legado)
   */
  decryptObject(obj, sensitiveFields = []) {
    if (!obj || typeof obj !== 'object') {
      return obj;
    }

    const result = { ...obj };
    let legacyFieldsFound = [];

    sensitiveFields.forEach(field => {
      if (result[field] && this.isEncrypted(result[field])) {
        // Tenta descriptografar dados legados v1
        if (this.needsMigration(result[field])) {
          result[field] = this.decrypt(result[field]);
          legacyFieldsFound.push(field);
        }
        // Dados v2 permanecem criptografados - backend descriptografa
      }
    });

    return result;
  }

  /**
   * Verifica se a criptografia frontend está desabilitada
   * @returns {boolean} True se desabilitada (sempre true agora)
   */
  isEncryptionDisabled() {
    return this._encryptionDisabled;
  }
}

// Predefined lists of sensitive fields for different data types
export const SENSITIVE_FIELDS = {
  API_SETTINGS: [
    'metaAccessToken',
    'metaAppSecret',
    'googleAdsAccessToken',
    'googleAdsClientSecret',
    'googleAdsDeveloperToken',
    'googleAdsLoginCustomerId',
    'metaRefreshToken',
    'googleAdsRefreshToken'
  ],
  USER_DATA: [
    'password',
    'refreshToken',
    'recoveryCode'
  ],
  CLIENT_DATA: [
    'apiKeys',
    'accessTokens',
    'clientPassword'
  ]
};

// ==================== CLOUD FUNCTION INTEGRATION ====================

/**
 * Serviço para migração de criptografia via Cloud Functions
 * Migra dados de v1 (XOR inseguro) para v2 (AES-256-GCM)
 */
class EncryptionMigrationService {
  constructor() {
    this._migrationInProgress = false;
  }

  /**
   * Verificar status de criptografia da agência
   * @param {string} agencyId - ID da agência
   * @returns {Promise<Object>} Status da criptografia
   */
  async checkStatus(agencyId) {
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();

      const checkEncryptionStatus = httpsCallable(functions, 'checkEncryptionStatus');
      const result = await checkEncryptionStatus({ agencyId });

      return result.data;
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Migrar dados da agência para criptografia v2
   * @param {string} agencyId - ID da agência
   * @returns {Promise<Object>} Resultado da migração
   */
  async migrate(agencyId) {
    if (this._migrationInProgress) {
      return {
        success: false,
        error: 'Migração já em andamento'
      };
    }

    try {
      this._migrationInProgress = true;

      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();

      const migrateEncryptionToV2 = httpsCallable(functions, 'migrateEncryptionToV2');
      const result = await migrateEncryptionToV2({ agencyId });

      return result.data;

    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    } finally {
      this._migrationInProgress = false;
    }
  }

  /**
   * Descriptografar dados via Cloud Function (para dados v2)
   * @param {string} agencyId - ID da agência
   * @param {string[]} fields - Campos a descriptografar
   * @returns {Promise<Object>} Dados descriptografados
   */
  async decryptViaCloudFunction(agencyId, fields) {
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();

      const decryptSensitiveData = httpsCallable(functions, 'decryptSensitiveData');
      const result = await decryptSensitiveData({ agencyId, fields });

      return result.data;
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Criptografar dados via Cloud Function
   * @param {Object} data - Dados a criptografar
   * @param {string[]} fields - Campos a criptografar
   * @returns {Promise<Object>} Dados criptografados
   */
  async encryptViaCloudFunction(data, fields) {
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();

      const encryptSensitiveData = httpsCallable(functions, 'encryptSensitiveData');
      const result = await encryptSensitiveData({ data, fields });

      return result.data;
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Verificar se há migração pendente e notificar usuário
   * @param {string} agencyId - ID da agência
   */
  async checkAndNotifyMigration(agencyId) {
    try {
      const status = await this.checkStatus(agencyId);

      if (status.success && status.status?.summary?.needsMigration) {
        // Disparar evento para UI mostrar notificação
        window.dispatchEvent(new CustomEvent('encryption-migration-needed', {
          detail: {
            agencyId,
            v1Count: status.status.summary.v1Legacy,
            unencryptedCount: status.status.summary.unencrypted
          }
        }));

        return true;
      }

      return false;
    } catch (error) {
      return false;
    }
  }
}

export const encryptionMigrationService = new EncryptionMigrationService();
export const encryptionService = new EncryptionService();
